import React, { useEffect, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowLeftIcon, ClockIcon, CalendarIcon } from 'lucide-react';
import { getBlogPost } from '../data/blogPosts';
import { openCalendlyPopup } from '../utils/calendly';
import { useLanguage } from '../contexts/LanguageContext';
import ReactMarkdown from 'react-markdown';
export function BlogPostPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = slug ? getBlogPost(slug) : undefined;
  const {
    language
  } = useLanguage();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Add structured data for blog post and breadcrumbs
  useEffect(() => {
    if (!post) return;
    // BlogPosting Schema
    const blogPostingSchema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title[language],
      description: post.excerpt[language],
      image: 'https://crmkraft.de/og-image.jpg',
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      author: {
        '@type': 'Person',
        name: post.author.name,
        jobTitle: post.author.role
      },
      publisher: {
        '@type': 'Organization',
        name: 'CRM Kraft',
        logo: {
          '@type': 'ImageObject',
          url: 'https://crmkraft.de/logo.png'
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://crmkraft.de/blog/${post.slug}`
      },
      articleSection: post.category[language],
      wordCount: post.content[language].split(' ').length,
      timeRequired: post.readTime
    };
    // Breadcrumb Schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: language === 'de' ? 'Home' : 'Home',
        item: 'https://crmkraft.de/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://crmkraft.de/blog'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: post.title[language],
        item: `https://crmkraft.de/blog/${post.slug}`
      }]
    };
    // Add BlogPosting schema
    let blogPostScriptTag = document.querySelector('script[data-blog-post-schema]');
    if (!blogPostScriptTag) {
      blogPostScriptTag = document.createElement('script');
      blogPostScriptTag.setAttribute('type', 'application/ld+json');
      blogPostScriptTag.setAttribute('data-blog-post-schema', 'true');
      document.head.appendChild(blogPostScriptTag);
    }
    blogPostScriptTag.textContent = JSON.stringify(blogPostingSchema);
    // Add Breadcrumb schema
    let breadcrumbScriptTag = document.querySelector('script[data-breadcrumb-schema]');
    if (!breadcrumbScriptTag) {
      breadcrumbScriptTag = document.createElement('script');
      breadcrumbScriptTag.setAttribute('type', 'application/ld+json');
      breadcrumbScriptTag.setAttribute('data-breadcrumb-schema', 'true');
      document.head.appendChild(breadcrumbScriptTag);
    }
    breadcrumbScriptTag.textContent = JSON.stringify(breadcrumbSchema);
    return () => {
      const blogPostTag = document.querySelector('script[data-blog-post-schema]');
      if (blogPostTag) blogPostTag.remove();
      const breadcrumbTag = document.querySelector('script[data-breadcrumb-schema]');
      if (breadcrumbTag) breadcrumbTag.remove();
    };
  }, [post, language]);
  if (!post) {
    return <div className="min-h-screen bg-[#0a0e1a]" data-id="element-416">
        <SEO title={language === 'de' ? 'Artikel nicht gefunden | CRM Kraft' : 'Article Not Found | CRM Kraft'} description={language === 'de' ? 'Der gesuchte Artikel wurde nicht gefunden.' : 'The article you are looking for was not found.'} noindex={true} data-id="element-417" />
        <Navigation data-id="element-418" />
        <main className="pt-24 pb-16" data-id="element-419">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-420">
            <h1 className="text-4xl font-bold text-white mb-4" data-id="element-421">
              {language === 'de' ? 'Artikel nicht gefunden' : 'Article not found'}
            </h1>
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#ff6b35] hover:underline" data-id="element-422">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-423" />
              {language === 'de' ? 'Zurück zum Blog' : 'Back to Blog'}
            </Link>
          </div>
        </main>
        <Footer data-id="element-424" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0a0e1a]" data-id="element-425">
      <SEO title={`${post.title[language]} | CRM Kraft Blog`} description={post.excerpt[language]} canonical={`https://crmkraft.de/blog/${post.slug}`} ogType="article" article={{
      publishedTime: post.publishedAt,
      author: post.author.name,
      section: post.category[language]
    }} data-id="element-426" />
      <Navigation data-id="element-427" />
      <main className="pt-24 pb-16" data-id="element-428">
        {/* Breadcrumb Navigation */}
        <nav className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8" aria-label="Breadcrumb" data-id="element-429">
          <ol className="flex items-center gap-2 text-sm text-gray-400" data-id="element-430">
            <li data-id="element-431">
              <Link to="/" className="hover:text-white transition-colors" data-id="element-432">
                {language === 'de' ? 'Home' : 'Home'}
              </Link>
            </li>
            <li className="flex items-center gap-2" data-id="element-433">
              <span data-id="element-434">/</span>
              <Link to="/blog" className="hover:text-white transition-colors" data-id="element-435">
                Blog
              </Link>
            </li>
            <li className="flex items-center gap-2" data-id="element-436">
              <span data-id="element-437">/</span>
              <span className="text-white truncate max-w-[200px] sm:max-w-none" data-id="element-438">
                {post.title[language]}
              </span>
            </li>
          </ol>
        </nav>

        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-439">
          {/* Back Link */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors" data-id="element-440">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-441" />
            {language === 'de' ? 'Zurück zum Blog' : 'Back to Blog'}
          </Link>

          {/* Category Badge */}
          <div className="inline-block bg-[#ff6b35]/10 text-[#ff6b35] text-sm font-semibold px-4 py-1.5 rounded-full mb-6" data-id="element-442">
            {post.category[language]}
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-443">
            {post.title[language]}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-8 pb-8 border-b border-white/10" data-id="element-444">
            <div className="flex items-center gap-2" data-id="element-445">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff6b35] to-[#ff5722] flex items-center justify-center text-white font-bold" data-id="element-446">
                {post.author.name.charAt(0)}
              </div>
              <div data-id="element-447">
                <div className="text-white font-medium" data-id="element-448">{post.author.name}</div>
                <div className="text-sm text-gray-500" data-id="element-449">{post.author.role}</div>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-450">
              <CalendarIcon className="w-4 h-4" data-id="element-451" />
              <time dateTime={post.publishedAt} data-id="element-452">
                {new Date(post.publishedAt).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')}
              </time>
            </div>
            <div className="flex items-center gap-2" data-id="element-453">
              <ClockIcon className="w-4 h-4" data-id="element-454" />
              <span data-id="element-455">
                {post.readTime} {language === 'de' ? 'Lesezeit' : 'read'}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none" data-id="element-456">
            <ReactMarkdown components={{
            h1: ({
              children
            }) => <h1 className="text-4xl font-bold text-white mb-6 mt-12" data-id="element-458">
                    {children}
                  </h1>,
            h2: ({
              children
            }) => <h2 className="text-3xl font-bold text-white mb-4 mt-10" data-id="element-459">
                    {children}
                  </h2>,
            h3: ({
              children
            }) => <h3 className="text-2xl font-bold text-white mb-3 mt-8" data-id="element-460">
                    {children}
                  </h3>,
            p: ({
              children
            }) => <p className="text-gray-300 leading-relaxed mb-6" data-id="element-461">
                    {children}
                  </p>,
            ul: ({
              children
            }) => <ul className="space-y-2 mb-6" data-id="element-462">{children}</ul>,
            li: ({
              children
            }) => <li className="text-gray-300 flex items-start gap-3" data-id="element-463">
                    <span className="text-[#ff6b35] mt-2" data-id="element-464">•</span>
                    <span data-id="element-465">{children}</span>
                  </li>,
            strong: ({
              children
            }) => <strong className="text-white font-semibold" data-id="element-466">
                    {children}
                  </strong>
          }} data-id="element-457">
              {post.content[language]}
            </ReactMarkdown>
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-br from-[#ff6b35]/20 to-[#ff6b35]/5 rounded-2xl border border-[#ff6b35]/30 p-8 lg:p-12 text-center" data-id="element-467">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-468">
              {language === 'de' ? 'Bereit für mehr Umsatz?' : 'Ready for More Revenue?'}
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto" data-id="element-469">
              {language === 'de' ? 'Lass uns in einem kostenlosen Beratungsgespräch analysieren, wie du dein CRM-System optimieren und deinen Vertrieb auf das nächste Level bringen kannst.' : "Let's analyze in a free consultation how you can optimize your CRM system and take your sales to the next level."}
            </p>
            <button onClick={openCalendlyPopup} className="inline-flex items-center gap-2 bg-[#ff6b35] hover:bg-[#ff5722] text-white px-8 py-4 rounded-lg font-semibold transition-colors" data-id="element-470">
              {language === 'de' ? 'Kostenlose Demo buchen' : 'Book Free Demo'}
            </button>
          </div>
        </article>
      </main>
      <Footer data-id="element-471" />
    </div>;
}